/**
 * Кто смотрит на страницу: аноним, вошедший или вошедший с оговоркой.
 *
 * Состояние входа отделено от состояния ленты нарочно. Лента переживает разрыв
 * связи и продолжает показывать последнее известное; вход — не переживает: если
 * доступ заморожен, показывать вчерашние имена контор нельзя ни секунды. Слитые
 * в один стор, эти два правила спорят при каждом переподключении.
 *
 * Причина, по которой лента не живая, важнее самого факта. «Демо» без
 * объяснения выглядит поломкой, и человек идёт в поддержку вместо того, чтобы
 * нажать одну кнопку в Telegram. Поэтому `reason` едет с сервера и показывается
 * словами.
 */

import { create } from 'zustand';

import { apiUrl } from '../lib/build';

const DEVICE_KEY = 'forq.device';

/**
 * Постоянный номер этого браузера. Не отпечаток и не попытка узнать человека:
 * сервер считает устройства, и без стабильного номера каждый вход выглядел бы
 * новым экраном — то есть честный человек упирался бы в лимит устройств,
 * просто зайдя дважды.
 *
 * Очистка хранилища его сбрасывает, и это принято: слабость закрывается
 * лимитом и остыванием на сервере, а не попыткой узнать браузер против его воли.
 */
export function deviceId(): string {
  try {
    const saved = localStorage.getItem(DEVICE_KEY);
    if (saved !== null && saved.length > 0) return saved;
    const fresh = crypto.randomUUID();
    localStorage.setItem(DEVICE_KEY, fresh);
    return fresh;
  } catch {
    return 'no-storage';
  }
}

/**
 * `guest` — вошёл, но ленты не видит: доступ открывает приглашение, а не сам
 * факт входа. `member` — приглашённый, `staff` — мы сами.
 */
export type Tier = 'anon' | 'guest' | 'member' | 'staff';

/** Почему лента не живая, хотя человек вошёл. */
export type Reason = 'frozen' | 'device_pending' | 'expired' | 'revoked' | null;

/**
 * Название того, что у человека есть, и одна строка о том, что это значит.
 *
 * Приходит с сервера целиком, а не собирается здесь по ярусу. Вкладка сканера
 * живёт неделями без перезагрузки: переименование, зашитое в бандл, доехало бы
 * до части людей — а название, которое у разных людей разное, хуже отсутствия
 * названия.
 */
export interface Plan {
  code: string;
  name: string;
  note: string;
}

export interface Me {
  authenticated: boolean;
  tier: Tier;
  /** Telegram id вошедшего. Пусто у анонима и у старого ответа сервера. */
  id: string | null;
  name: string | null;
  username: string | null;
  plan: Plan | null;
  reason: Reason;
  /**
   * Право на живую ленту. Приходит с сервера отдельным полем, а не выводится
   * здесь из яруса: вкладка живёт неделями, и новый ярус на сервере не должен
   * молча получать права в старом бандле.
   */
  access: boolean;
  /** До какого числа открыт доступ; `null` — бессрочно либо доступа нет. */
  until: string | null;
  /** Есть ли вообще вход на этом контуре: бот может быть не настроен. */
  possible: boolean;
  botName: string | null;
  /** Кнопка call. Только четверо, поле с сервера, не угадываем по ярусу. */
  caller: boolean;
  /**
   * Стол `/admin`. Только Рафик и Удмурт, поле с сервера.
   * Staff этим правом не считается: куратор видит ленту, не чужие журналы.
   */
  owner: boolean;
  /** Кураторская `/curator`: Сергей, Михаил и владельцы. Поле с сервера. */
  curator: boolean;
}

export const ANON: Me = {
  authenticated: false,
  tier: 'anon',
  id: null,
  name: null,
  username: null,
  plan: null,
  reason: null,
  access: false,
  until: null,
  possible: false,
  botName: null,
  caller: false,
  owner: false,
  curator: false,
};

export interface Waiting {
  code: string;
  link: string;
  /** Когда код перестанет работать, мс эпохи. */
  until: number;
}

export interface SessionState {
  me: Me;
  /** `null` — ещё не спрашивали. Пока не спросили, экран входа не показываем. */
  known: boolean;
  waiting: Waiting | null;
  error: string | null;

  load: (force?: boolean) => Promise<void>;
  begin: () => Promise<void>;
  poll: () => Promise<boolean>;
  cancel: () => void;
  logout: () => Promise<void>;
}

function readMe(body: unknown): Me {
  const raw = (body ?? {}) as Record<string, unknown>;
  const login = (raw.login ?? {}) as Record<string, unknown>;
  const tier =
    raw.tier === 'member' || raw.tier === 'staff' || raw.tier === 'guest' ? raw.tier : 'anon';
  const why = raw.reason ?? ((raw.access ?? {}) as Record<string, unknown>).why;
  const reason =
    why === 'frozen' || why === 'device_pending' || why === 'expired' || why === 'revoked'
      ? why
      : null;
  const card = (raw.plan ?? null) as Record<string, unknown> | null;
  const right = (raw.access ?? null) as Record<string, unknown> | null;
  return {
    authenticated: raw.authenticated === true,
    tier,
    id: typeof raw.id === 'string' ? raw.id : null,
    name: typeof raw.name === 'string' ? raw.name : null,
    username: typeof raw.username === 'string' ? raw.username : null,
    plan:
      card !== null && typeof card.name === 'string'
        ? {
            code: typeof card.code === 'string' ? card.code : '',
            name: card.name,
            note: typeof card.note === 'string' ? card.note : '',
          }
        : null,
    reason,
    // Ответ старого сервера (во время выката рядом живут две версии) поля
    // `access` не содержит вовсе. Тогда правом считается сам факт входа — так
    // вело себя всё до приглашений, и это безопаснее обратного: вошедший
    // человек на минуту выката увидит ленту, а не запертую дверь.
    access: right === null ? raw.authenticated === true : right.ok === true,
    until: right !== null && typeof right.until === 'string' ? right.until : null,
    possible: (login.enabled === true && login.bot === true) || raw.authenticated === true,
    botName: typeof login.botUsername === 'string' ? login.botUsername : null,
    caller: raw.caller === true,
    owner: raw.owner === true,
    curator: raw.curator === true,
  };
}

export const useSession = create<SessionState>((set, get) => ({
  me: ANON,
  known: false,
  waiting: null,
  error: null,

  /**
   * Спросить сервер, кто смотрит.
   *
   * Повторный вызов в той же жизни страницы ничего не делает: дверь спрашивает
   * первой, экран за ней — второй, и два одинаковых запроса подряд стоили бы
   * лишнего кругового пути ровно там, где человек ждёт ленту. Насильно —
   * `load(true)`: после выхода и после смены состояния ответ обязан быть новым.
   */
  load: async (force = false) => {
    if (get().known && !force) return;
    const ac = new AbortController();
    const timer = setTimeout(() => ac.abort(), 2500);
    try {
      const res = await fetch(apiUrl('/v1/me'), { credentials: 'same-origin', signal: ac.signal });
      // 404 — на этом контуре входа нет вовсе. Это не ошибка и не повод
      // показывать кнопку, которая никуда не приведёт.
      if (res.status === 404) {
        set({ me: ANON, known: true });
        return;
      }
      set({ me: readMe(await res.json()), known: true });
    } catch {
      // Обрыв и таймаут — не вечный #boot. Повторим: иначе медленный /v1/me
      // оставляет человека на «собираем вилки» без ленты и без двери.
      set({ known: true });
      window.setTimeout(() => {
        void get().load(true);
      }, 1500);
    } finally {
      clearTimeout(timer);
    }
  },

  begin: async () => {
    set({ error: null });
    try {
      const res = await fetch(apiUrl('/auth/telegram/start'), {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ deviceId: deviceId() }),
      });
      const body = (await res.json()) as Record<string, unknown>;
      if (!res.ok || typeof body.code !== 'string' || typeof body.link !== 'string') {
        set({ error: 'вход сейчас недоступен' });
        return;
      }
      const ttl = typeof body.ttlSeconds === 'number' ? body.ttlSeconds : 300;
      set({ waiting: { code: body.code, link: body.link, until: Date.now() + ttl * 1000 } });
    } catch {
      set({ error: 'нет связи с сервером' });
    }
  },

  /** Спросить, нажал ли человек «начать» у бота. `true` — вошли. */
  poll: async () => {
    const waiting = get().waiting;
    if (waiting === null) return false;
    if (Date.now() > waiting.until) {
      set({ waiting: null, error: 'код истёк, начните заново' });
      return false;
    }
    try {
      const res = await fetch(apiUrl(`/auth/telegram/status?code=${waiting.code}`), {
        credentials: 'same-origin',
      });
      const body = (await res.json()) as Record<string, unknown>;
      if (body.authorized !== true) return false;
      set({ me: readMe(body), known: true, waiting: null, error: null });
      return true;
    } catch {
      return false;
    }
  },

  cancel: () => set({ waiting: null, error: null }),

  logout: async () => {
    try {
      await fetch(apiUrl('/auth/logout'), { method: 'POST', credentials: 'same-origin' });
    } catch {
      // Даже если сервер не ответил, на этом экране человек уже вышел: держать
      // его внутри из-за сетевой ошибки — худшее из поведений.
    }
    set({ me: { ...ANON, possible: get().me.possible, botName: get().me.botName } });
  },
}));
